package diana.soleil.design.iraniancanadienbazzar.feature.chat

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppSearchBar
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.NotificationBadge
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.avatarColorFor
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.Conversation
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_error
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_channels_section
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_messages_section
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_new_message_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_people_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_requests_section
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_retry
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_search_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_title
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.chat_unnamed_member
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.chat_time_just_now
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.chat_time_minutes_ago
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.chat_time_hours_ago
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.chat_time_days_ago
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.chat_time_weeks_ago
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_unread_a11y
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/**
 * Stateful inbox entry. Guests are bounced to sign-in ([onRequireSignIn]) — chat is account-only.
 * Tapping a thread opens it via [onOpenConversation].
 */
@Composable
fun InboxRoute(
    onBack: () -> Unit,
    onOpenConversation: (conversationId: String) -> Unit,
    onNewMessage: () -> Unit,
    onOpenPeople: () -> Unit = {},
    // Find-your-people is kill-switchable (feature_people_enabled); the icon hides when off.
    onRequireSignIn: () -> Unit,
    onOpenChannel: (businessId: String) -> Unit = {},
    modifier: Modifier = Modifier,
    viewModel: InboxViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    LaunchedEffect(state.signInRequired) {
        if (state.signInRequired) onRequireSignIn()
    }
    InboxScreen(
        state = state,
        onBack = onBack,
        onQueryChange = viewModel::onQueryChange,
        onOpenConversation = { conversationId ->
            viewModel.markConversationRead(conversationId)
            onOpenConversation(conversationId)
        },
        onNewMessage = onNewMessage,
        onOpenPeople = onOpenPeople.takeIf { org.koin.compose.koinInject<diana.soleil.design.iraniancanadienbazzar.core.data.FeatureFlags>().available.collectAsState().value.people },
        onOpenChannel = onOpenChannel,
        onRetry = viewModel::retry,
        modifier = modifier,
    )
}

/**
 * Messages — Inbox (Stitch `f13a78d4`): teal top bar, search-conversations field, thread rows
 * (initial-avatar tile, business name, last-message preview, server-backed unread badge). Compose FAB
 * remains a later "pick a business" flow. Tokens + string resources only.
 */
@Composable
fun InboxScreen(
    state: InboxUiState,
    onBack: () -> Unit,
    onQueryChange: (String) -> Unit,
    onOpenConversation: (conversationId: String) -> Unit,
    onNewMessage: () -> Unit = {},
    onOpenPeople: (() -> Unit)? = null,
    onOpenChannel: (businessId: String) -> Unit = {},
    onRetry: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.background(AppTheme.colors.background).appContentWidth(AppTheme.sizes.contentMaxWidth).fillMaxHeight().navigationBarsPadding()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.primaryContainer)
                .statusBarsPadding()
                .padding(horizontal = AppTheme.spacing.lg, vertical = AppTheme.spacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.inbox_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onPrimary,
            )
            Text(
                text = stringResource(Res.string.inbox_title),
                style = AppTheme.typography.headlineMd,
                color = AppTheme.colors.onPrimary,
                modifier = Modifier.weight(1f),
            )
            if (onOpenPeople != null) {
                AppIconButton(
                    iconName = "group",
                    contentDescription = stringResource(Res.string.inbox_people_a11y),
                    onClick = onOpenPeople,
                    tint = AppTheme.colors.onPrimary,
                )
            }
            AppIconButton(
                iconName = "edit_square",
                contentDescription = stringResource(Res.string.inbox_new_message_a11y),
                onClick = onNewMessage,
                tint = AppTheme.colors.onPrimary,
            )
        }

        when {
            state.loading -> LoadingState()
            state.error -> ErrorState(
                message = stringResource(Res.string.inbox_error),
                onRetry = onRetry,
                retryLabel = stringResource(Res.string.inbox_retry),
            )
            else -> LazyColumn(
                contentPadding = PaddingValues(
                    start = AppTheme.spacing.lg,
                    end = AppTheme.spacing.lg,
                    top = AppTheme.spacing.md,
                    bottom = AppTheme.spacing.xl,
                ),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                item {
                    AppSearchBar(
                        value = state.query,
                        onValueChange = onQueryChange,
                        placeholder = stringResource(Res.string.inbox_search_placeholder),
                        modifier = Modifier.padding(bottom = AppTheme.spacing.sm),
                    )
                }
                // Community channels of followed businesses — a labelled rail above the threads.
                if (state.query.isBlank() && state.channels.isNotEmpty()) {
                    item {
                        SectionLabel(text = stringResource(Res.string.inbox_channels_section))
                    }
                    items(count = state.channels.size, key = { "ch_" + state.channels[it].businessId }) { index ->
                        val channel = state.channels[index]
                        ChannelRow(channel = channel, onClick = { onOpenChannel(channel.businessId) })
                    }
                    if (state.requests.isEmpty() && state.filtered.isNotEmpty()) {
                        item {
                            SectionLabel(text = stringResource(Res.string.inbox_messages_section))
                        }
                    }
                }
                // Message requests (pending DMs) get their own labelled rail above the main inbox.
                if (state.query.isBlank() && state.requests.isNotEmpty()) {
                    item {
                        SectionLabel(
                            text = stringResource(Res.string.inbox_requests_section, state.requests.size),
                        )
                    }
                    items(count = state.requests.size, key = { "req_" + state.requests[it].id }) { index ->
                        val request = state.requests[index]
                        ConversationRow(
                            conversation = request,
                            onClick = { onOpenConversation(request.id) },
                        )
                    }
                    item {
                        SectionLabel(text = stringResource(Res.string.inbox_messages_section))
                    }
                }
                if (state.filtered.isEmpty() && state.requests.isEmpty()) {
                    item {
                        EmptyState(
                            title = stringResource(Res.string.inbox_empty_title),
                            description = stringResource(Res.string.inbox_empty_desc),
                        )
                    }
                } else {
                    items(count = state.filtered.size, key = { state.filtered[it].id }) { index ->
                        val conversation = state.filtered[index]
                        ConversationRow(
                            conversation = conversation,
                            onClick = { onOpenConversation(conversation.id) },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun SectionLabel(text: String) {
    Text(
        text = text,
        style = AppTheme.typography.labelMd,
        color = AppTheme.colors.onSurfaceVariant,
        modifier = Modifier.padding(top = AppTheme.spacing.sm, bottom = AppTheme.spacing.xxs),
    )
}

/** One followed community channel: campaign glyph on the business's identity color + last post. */
@Composable
private fun ChannelRow(
    channel: diana.soleil.design.iraniancanadienbazzar.core.model.Channel,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .background(AppTheme.colors.surface, AppTheme.shapes.card)
            .padding(AppTheme.spacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        Box(
            modifier = Modifier
                .size(AppTheme.sizes.avatarMd)
                .background(avatarColorFor(channel.businessId), AppTheme.shapes.tile),
            contentAlignment = Alignment.Center,
        ) {
            AppIcon(
                name = "campaign",
                tint = AppTheme.colors.onPrimary,
            )
        }
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs)) {
            Text(
                text = channel.name,
                style = AppTheme.typography.bodyLg,
                color = AppTheme.colors.onSurface,
            )
            channel.lastPostText?.takeIf { it.isNotBlank() }?.let {
                Text(
                    text = it,
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                    maxLines = 1,
                )
            }
        }
    }
}

@Composable
private fun ConversationRow(conversation: Conversation, onClick: () -> Unit) {
    // Threads created before 2026-09-17 can carry an empty snapshotted name (email/password accounts
    // had no Auth display name), which rendered as a blank row and a blank avatar initial.
    val title = conversation.businessName.ifBlank { stringResource(Res.string.chat_unnamed_member) }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .background(AppTheme.colors.surface, AppTheme.shapes.card)
            .padding(AppTheme.spacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        Box(
            modifier = Modifier
                .size(AppTheme.sizes.avatarMd)
                .background(avatarColorFor(conversation.id), AppTheme.shapes.tile),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = title.take(1).uppercase(),
                style = AppTheme.typography.headlineMd,
                color = AppTheme.colors.onPrimary,
            )
        }
        // Unread threads get visual weight: emphasized name + darker, semibold preview.
        val unread = conversation.unreadCount > 0
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs)) {
            Text(
                text = title,
                style = if (unread) AppTheme.typography.headlineMd else AppTheme.typography.bodyLg,
                color = AppTheme.colors.onSurface,
            )
            if (conversation.lastMessage.isNotBlank()) {
                Text(
                    text = conversation.lastMessage,
                    style = if (unread) AppTheme.typography.labelMd else AppTheme.typography.bodySm,
                    color = if (unread) AppTheme.colors.onSurface else AppTheme.colors.onSurfaceVariant,
                    maxLines = 1,
                )
            }
        }
        Column(
            horizontalAlignment = Alignment.End,
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
        ) {
            conversation.lastMessageAtMillis?.let { millis ->
                Text(
                    text = inboxTimeLabel(millis),
                    style = AppTheme.typography.labelSm,
                    color = if (unread) AppTheme.colors.primary else AppTheme.colors.onSurfaceVariant,
                )
            }
            if (conversation.unreadCount > 0) {
                NotificationBadge(
                    count = conversation.unreadCount,
                    contentDescription = stringResource(
                        Res.string.inbox_unread_a11y,
                        conversation.unreadCount,
                    ),
                )
            } else {
                AppIcon(
                    name = "chevron_forward",
                    size = AppTheme.sizes.iconSm,
                    tint = AppTheme.colors.onSurfaceVariant,
                )
            }
        }
    }
}

/** Compact relative time for an inbox row (e.g. "3h", "2d"); bucketed to the largest unit. */
@Composable
private fun inboxTimeLabel(millis: Long): String {
    val minutes = ((kotlin.time.Clock.System.now().toEpochMilliseconds() - millis) / 60_000L).coerceAtLeast(0)
    val lang = AppTheme.language
    return when {
        minutes < 1 -> stringResource(Res.string.chat_time_just_now)
        minutes < 60 -> stringResource(Res.string.chat_time_minutes_ago, lang.formatCount(minutes))
        minutes < 60 * 24 -> stringResource(Res.string.chat_time_hours_ago, lang.formatCount(minutes / 60))
        minutes < 60 * 24 * 7 -> stringResource(Res.string.chat_time_days_ago, lang.formatCount(minutes / (60 * 24)))
        else -> stringResource(Res.string.chat_time_weeks_ago, lang.formatCount(minutes / (60 * 24 * 7)))
    }
}
